import { Component, computed, Inject, OnDestroy, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhSidePanelService, SIDE_PANEL_DATA } from '../../services/ph-side-panel.service';
import { SearchComponent } from '../search/search.component';
import { MaterialModule } from '../../modules/material.module';
import { ISearchSettings } from '../../../product/models/models';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { SessionService } from '../../services/session.service';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { FormsModule } from '@angular/forms';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { Stand } from '../../../plan/models/stand';
import { Utils } from '../../services/utils.service';
import { SearchAlgorithmType } from '../../models/personalSettings';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { ICreateGroupData, IPlanogramGroupItem, IPlanogramListItem, IPlanPlanogramGroup } from '../../models/planogramGroups';
import { ITableData } from '../../models/table';
import { RequestParams } from '../../models/requestParams';
import { lastValueFrom, ReplaySubject, Subscription, switchMap } from 'rxjs';
import { FiltersPanelService } from '../filters-panel/filters-panel.service';
import { Filter, FilterID, FilterItem, FilterType, setDefaults } from '../filters-panel/models/filter';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { FiltersService } from '../filters-panel/filters.service';
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { FiltersPanelComponent } from "../filters-panel/filters-panel.component";
import { BoolSelectingStrategy } from '../filters-panel/models/strategy';
import { LoaderComponent } from "../loader/loader.component";
import { TableService } from '../../services/table.service';
import { PlanogramGroupsService } from '../../services/planogram-groups.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { RequiredFieldPipe } from '../../pipes/required-field.pipe';
import { MixpanelService } from '../../services/mixpanel.service';
import { MixpanelEvents } from '../../models/mixpanelEvents';

interface IPlanogramGroupsPanelData {
	planograms: Array<Stand>;
	planId?: number;
	group?: IPlanPlanogramGroup;
	highlightPlanogramId?: number;
	hideEditLink?: boolean;
	onCloseResult?: (result: { changed: boolean; deleted: boolean }) => void;
}

@Component({
	selector: 'app-planogram-groups-panel',
	templateUrl: './planogram-groups-panel.component.html',
	styleUrl: './planogram-groups-panel.component.scss',
	providers: [FiltersPanelService],
	imports: [
		TranslateModule,
		PhIconWrapperComponent,
		SearchComponent,
		MaterialModule,
		PhIconComponent,
		PhButtonComponent,
		PhLoaderComponent,
		FormsModule,
		SanitizerPipe,
		FaIconComponent,
		FiltersPanelComponent,
		LoaderComponent,
		RouterLink,
		GetLinkPipe,
		ReactiveFormsModule,
		RequiredFieldPipe
	]
})
export class PlanogramGroupsPanelComponent implements OnInit, OnDestroy {

	@ViewChild('form') formElement: NgForm;

	private _loadAddPlanograms$: ReplaySubject<void> = new ReplaySubject<void>(1);
	private _filters: Array<Filter> = [];
	private _requestParams: RequestParams;
	private _subscriptions: Array<Subscription> = [];

	protected editAccess: boolean = false;
	protected group: IPlanPlanogramGroup;
	protected hideEditLink: boolean = false;
	protected highlightPlanogramId: number;
	protected maxPlanogramsCount: number;
	protected planId: number;
	protected filtersApplied: boolean = false;
	
	protected addPlanogramsResponse: WritableSignal<ITableData<IPlanogramListItem>> = signal(null);
	protected readonly formGroup = new FormGroup({
		name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(200)] })
	});
	protected hasChanges: WritableSignal<boolean> = signal(false);
	protected formSubmitted: WritableSignal<boolean> = signal(false);
	protected groupNameInvalid: WritableSignal<boolean> = signal(false);
	protected isAddLoading: WritableSignal<boolean> = signal(false);
	protected isAddLoadingNext: WritableSignal<boolean> = signal(false);
	protected isLoading: WritableSignal<boolean> = signal(false);
	protected searchSettings: WritableSignal<Array<ISearchSettings>> = signal([]);
	protected selectedPlanograms: WritableSignal<Array<IPlanogramGroupItem>> = signal([]);
	protected selectedSearchQuery: WritableSignal<string> = signal('');

	protected applyButton: Signal<{ label: string }> = computed(() => {
        let label: string;
        if (!this.group?.id) {
            label = 'PLANOGRAM_GROUPS.UNITE_PLANOGRAMS';
        } else {
            label = this.selectedPlanogramsCount() > 1 ? 'COMMON.APPLY' : 'PLANOGRAM_GROUPS.SEPARATE_PLANOGRAMS';
        }
		return { label };
	});

	protected addPlanograms: Signal<Array<IPlanogramGroupItem>> = computed(() => {
		return this.addPlanogramsResponse()?.results.dataSource.map((planogram: IPlanogramListItem) => ({
			id: planogram.key,
			name: planogram.name,
			number: planogram.number,
			image: planogram.image,
			group: planogram.group_name,
            plans: planogram.plan_names?.length > 1 ? planogram.plan_names.join('\n') : ''
		})) || [];
	});

	protected addPlanogramsCount: Signal<number> = computed(() => {
		return this.addPlanogramsResponse()?.count || 0;
	});

	protected selectedPlanogramsCount: Signal<number> = computed(() => {
		return this.selectedPlanograms().length;
	});

	protected applyButtonDisabled: Signal<boolean> = computed(() => {
		return !this.hasChanges()
			|| this.selectedPlanogramsCount() > this.maxPlanogramsCount
			|| (!this.group?.id && this.selectedPlanogramsCount() <= 1)
			|| (
				this.formSubmitted()
				&& this.selectedPlanogramsCount() > 1
				&& this.groupNameInvalid()
			);
	});

	protected searchFields: Signal<Array<string>> = computed(() => {
		return this.searchSettings()
			.filter((option: ISearchSettings) => option.selected || !this.searchSettingsActive())
			.map((option: ISearchSettings) => option.name);
	});

	protected filteredSelectedPlanograms: Signal<Array<IPlanogramGroupItem>> = computed(() => {
		const searchAlgorithm: SearchAlgorithmType = this.sessionService.personalSettings.searchAlgorithm;

		return this.selectedPlanograms().filter((planogram: IPlanogramGroupItem) => {
			return Utils.searchItem(planogram, this.searchFields(), this.selectedSearchQuery(), searchAlgorithm);
		});
	});

	protected searchSettingsActive: Signal<boolean> = computed(() => {
		const selectedCount: number = this.searchSettings().filter((option: ISearchSettings) => option.selected).length;
		return selectedCount > 0 && selectedCount !== this.searchSettings().length;
	});

	constructor(
		@Inject(SIDE_PANEL_DATA) private data: IPlanogramGroupsPanelData,
		private sessionService: SessionService,
		private panelService: PhSidePanelService,
		private planogramsGroupsService: PlanogramGroupsService,
		private filtersPanelService: FiltersPanelService,
		private filtersService: FiltersService,
		private tableService: TableService,
		private snackBarService: SnackBarService,
		private dialog: MatDialog,
		private router: Router,
		private mixpanelService: MixpanelService
	) {
		this.planId = data.planId;
		this.group = data.group;
		this.hideEditLink = data.hideEditLink ?? false;
		this.hasChanges.set(!data.group?.id);
		this.formGroup.controls.name.setValue(this.group?.name ?? '');
		this.highlightPlanogramId = data.highlightPlanogramId;
		this.maxPlanogramsCount = this.sessionService.defaults.max_planograms_in_planogram_group;
		this.editAccess = this.sessionService.hasAccess('ph-planogram-edit');
		this._requestParams = new RequestParams({
			page_size: 30,
			plan_id: this.planId
		});
		this.searchSettings.set([
			{
				title: 'COMMON.PLANOGRAM.SEARCH_BY_NAME',
				selected: false,
				name: 'name'
			},
			{
				title: 'COMMON.PLANOGRAM.SEARCH_BY_NUMBER',
				selected: false,
				name: 'number'
			}
		]);
	}

	ngOnInit(): void {
		this._subscriptions.push(
			this.formGroup.controls.name.valueChanges.subscribe(() => {
				this.hasChanges.set(true);
				this.groupNameInvalid.set(this.formGroup.controls.name.invalid);
			})
		);

		if (this.group?.id) {
			this.isLoading.set(true);
			this.planogramsGroupsService.getPlanogramsList({ group_id: this.group.id, plan_id: this.planId }).subscribe({
				next: (response: ITableData<IPlanogramListItem>) => {
					const items: Array<IPlanogramGroupItem> = response.results.dataSource.map((item: IPlanogramListItem) => ({
						id: item.key,
						group: item.group_name,
						image: item.image,
						number: item.number,
						name: item.name,
                        plans: item.plan_names?.length > 1 ? item.plan_names.join('\n') : ''
					}));
					this.selectedPlanograms.set(items);
					this._requestParams.updateParams({
						exclude_ids: this.selectedPlanograms().map((planogram: IPlanogramGroupItem) => planogram.id).join(',')
					});
					this.loadAddPlanograms();
					this.isLoading.set(false);
				},
				error: () => {
					this.snackBarService.setErrorNotification();
					this.onClose();
				}
			})
		} else {
			this._requestParams.updateParams({
				exclude_ids: this.data.planograms.map((planogram: Stand) => planogram.main_stand).join(',')
			});
			if (this.data?.planograms?.length > 0) {
				const planogramItems: Array<IPlanogramGroupItem> = this.data.planograms.map((planogram: Stand) => ({
					id: planogram.main_stand,
					name: planogram.name,
					number: planogram.number,
					image: planogram.image ?? planogram.instrument.image,
					group: planogram.planogram_groups?.map((group: IPlanPlanogramGroup) => group.name).join(', ') || '',
                    plans: planogram.library?.length > 1 ? planogram.library.map(plan => plan.planogram_name).join('\n') : ''
				}));
				this.selectedPlanograms.set(planogramItems);
			}
			this.loadAddPlanograms();
		}
	}

	private loadAddPlanograms(): void {
		if (!this.editAccess) return;
		this._loadAddPlanograms$
			.pipe(
				switchMap(() => {
					this.isAddLoading.set(true);
					return this.planogramsGroupsService.getPlanogramsList(this._requestParams.getParams());
				})
			)
			.subscribe((response: ITableData<IPlanogramListItem>) => {
				this.addPlanogramsResponse.set(response);
				this.isAddLoading.set(false);
			});

		this._loadAddPlanograms$.next();
	}

	private onSeparate(): void {
		if (!this.group?.id) {
			this.onClose();
			return;
		}

		this.isLoading.set(true);
		const isSeparate: boolean = this.selectedPlanogramsCount() === 1;
		this.planogramsGroupsService.onDelete(this.group.id).subscribe({
			next: () => {
				this.isLoading.set(false);
				this.mixpanelService.trackPage(MixpanelEvents.PlanogramGroupSeparate, {
					groupId: this.group.id,
					planId: this.planId,
					planogramsCount: this.selectedPlanogramsCount()
				});
				this.onClose({ changed: false, deleted: true, separateId: isSeparate ? this.selectedPlanograms()[0].id : null });
			},
			error: () => {
				this.isLoading.set(false);
				this.snackBarService.setErrorNotification();
			}
		});
	}

	protected onSeparateGroup(): void {
		if (!this.group?.id) return;

		this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			backdropClass: 'back-drop',
			data: {
				label: 'PLANOGRAM_GROUPS.CONFIRM_SEPARATE_GROUP',
				twoAnswers: true,
				confirmLabel: 'PLANOGRAM_GROUPS.SEPARATE_GROUP',
				cancelLabel: 'COMMON.CANCEL'
			}
		}).afterClosed().subscribe((result: IConfirmModalAnswer | undefined) => {
			if (!result?.answer) return;
			this.onSeparate();
		});
	}

	protected onUnite(): void {
		if (this.selectedPlanogramsCount() > this.maxPlanogramsCount) return;

		this.trimGroupName();
		this.groupNameInvalid.set(this.formGroup.controls.name.invalid);
		if (this.formGroup.invalid) return;

		const params: ICreateGroupData = {
			planograms: this.selectedPlanograms().map((planogram: IPlanogramGroupItem) => planogram.id),
			name: this.formGroup.controls.name.value,
			plan_id: this.planId
		};

		if (this.group?.id) {
			this.isLoading.set(true);
			this.planogramsGroupsService.onUpdate(this.group.id, params).subscribe({
				next: () => {
					this.isLoading.set(false);
					this.mixpanelService.trackPage(MixpanelEvents.PlanogramGroupUpdate, {
						groupId: this.group.id,
						planId: this.planId,
						planogramsCount: this.selectedPlanogramsCount()
					});
					this.onClose({ changed: true, deleted: false });
				},
				error: () => {
					this.isLoading.set(false);
					this.showSaveErrorNotification();
				}
			});
        } else {
			if (this.selectedPlanograms().some((planogram: IPlanogramGroupItem) => !!planogram.group)) {
				this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '574px',
					backdropClass: 'back-drop',
					data: {
						label: 'PLANOGRAM_GROUPS.CONFIRM_UNITE_WITH_EXISTING_GROUPS',
						twoAnswers: true,
						confirmLabel: 'COMMON.CONFIRM',
						cancelLabel: 'COMMON.CANCEL'
					}
				}).afterClosed().subscribe((result: IConfirmModalAnswer | undefined) => {
					if (!result?.answer) return;
					this.createPlanogramGroup(params, true);
				});
			} else {
				this.createPlanogramGroup(params, false);
			}
        }
	}

	private createPlanogramGroup(params: ICreateGroupData, hadExistingGroups: boolean): void {
		this.isLoading.set(true);
		this.planogramsGroupsService.onCreate(params).subscribe({
			next: (response: { id: number }) => {
				this.isLoading.set(false);
				this.mixpanelService.trackPage(MixpanelEvents.PlanogramGroupCreate, {
					planId: this.planId,
					planogramsCount: params.planograms.length,
					hadExistingGroups
				});
				this.onClose({ changed: false, deleted: false });
				void this.router.navigate(
					this.sessionService.getLink(['stand', 'group', 'edit', response.id]),
					{ queryParams: { planogram: this.planId } }
				);
			},
			error: () => {
				this.isLoading.set(false);
				this.showSaveErrorNotification();
			}
		});
	}

	private showSaveErrorNotification(): void {
		this.snackBarService.setSnackBarNotification({
			messageText: 'PLANOGRAM_GROUPS.SAVE_ERROR',
			durationSeconds: 0,
			closeButton: true,
			closeActions: [
				{
					action: 'ERROR.TO_SUPPORT',
					callback: (): void => {
						window.$crisp.push(['do', 'chat:open']);
					}
				}
			]
		});
	}

	protected onAdd(planogram: IPlanogramGroupItem): void {
		if (planogram.group) {
			this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				backdropClass: 'back-drop',
				data: {
					label: 'PLANOGRAM_GROUPS.ALREADY_IN_GROUP',
					labelTranslateParams: { group: planogram.group },
					twoAnswers: true,
					confirmLabel: 'COMMON.CONFIRM',
					cancelLabel: 'COMMON.CANCEL'
				}
			}).afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result.answer) return;
				this.addPlanogramToSelection(planogram);
			});
		} else {
            this.addPlanogramToSelection(planogram);
        }
	}

	private addPlanogramToSelection(planogram: IPlanogramGroupItem): void {
		this.hasChanges.set(true);
		this.selectedPlanograms.update(value => [planogram, ...value]);
		this._requestParams.updateParams({
			exclude_ids: this.selectedPlanograms().map((planogram: IPlanogramGroupItem) => planogram.id).join(',')
		});
		this.addPlanogramsResponse.update((value: ITableData<IPlanogramListItem>) => {
			return {
				...value,
				count: value.count - 1,
				results: {
					...value.results,
					dataSource: value.results.dataSource.filter((p: IPlanogramListItem) => planogram.id !== p.key)
				}
			}
		});
		if (this.addPlanogramsResponse().next && this.addPlanograms().length < 15) {
			this.addPlanogramsResponse.set(null);
			this._loadAddPlanograms$.next();
		}
	}

	protected onDelete(id: number): void {
		this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			backdropClass: 'back-drop',
			data: {
				label: 'PLANOGRAM_GROUPS.CONFIRM_UNLINK_FROM_GROUP',
				twoAnswers: true,
				confirmLabel: 'COMMON.CONFIRM',
				cancelLabel: 'COMMON.CANCEL'
			}
		}).afterClosed().subscribe((result: IConfirmModalAnswer | undefined) => {
			if (!result?.answer) return;
			this.hasChanges.set(true);
            this.selectedPlanograms.update(value => value.filter((planogram: IPlanogramGroupItem) => planogram.id !== id));
            this._requestParams.updateParams({
                exclude_ids: this.selectedPlanograms().map((planogram: IPlanogramGroupItem) => planogram.id).join(',')
            });
            this._loadAddPlanograms$.next();
		});
	}

	protected onApplyClick(): void {
		if (this.selectedPlanogramsCount() > 1) {
			this.formSubmitted.set(true);
			this.trimGroupName();
			this.groupNameInvalid.set(this.formGroup.controls.name.invalid);
			this.formElement.onSubmit(undefined);
			return;
		}

		this.onSeparate();
	}

	private trimGroupName(): void {
		this.formGroup.controls.name.setValue(this.formGroup.controls.name.value.trim(), { emitEvent: false });
	}

	protected onSearch(value: string): void {
		this.selectedSearchQuery.set(value);
	}

	protected onSearchAdd(value: string = null): void {
		if (Utils.isNullOrUndefined(value)) value = this._requestParams.getParam('search');
		this._requestParams.updateParams({
			search: value,
			search_fields: this.searchFields().join(',')
		});
		this._loadAddPlanograms$.next();
	}

	protected onSearchSettingChange(option: ISearchSettings): void {
		this.searchSettings.update(value => value.map((item: ISearchSettings) => {
			if (item.name === option.name) {
				return {
					...item,
					selected: !item.selected
				};
			}
			return item;
		}));
	}

	public clearFilters(): void {
		this.filtersPanelService.setApplyPanel(false);
	}

	public async openFiltersPanel(): Promise<void> {
		this.filtersPanelService.setShowPanel(true);
        if (this._filters.length) {
            setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
			this.filtersPanelService.setFilters(this._filters);
            return;
        }
	
		const originalCategories: Array<FilterItem> = [];
		const originalBrands: Array<FilterItem> = [];
		const originalStatuses: Array<FilterItem> = (await this.filtersService.loadStatusTranslates())
			.map((status: { id: string, name: string }) => ({
				id: status.id,
				value: status.name,
				children: [],
				opened: false,
				hasChildren: false
			}));
		const originalEquipmentTypes: Array<FilterItem> = [];
		
		if (this.sessionService.hasFeature(ServiceFeatures.Categories)) {
			originalCategories.push(...await lastValueFrom(this.filtersService.getCategories()));
		}
		
		if (this.sessionService.hasFeature(ServiceFeatures.Brands)) {
			originalBrands.push(...await lastValueFrom(this.filtersService.getBrands()));
		}
		
		originalEquipmentTypes.push(...await lastValueFrom(this.filtersService.getEquipmentTypes({ used: true })));
		
		if (originalCategories.length) {
			this._filters.push(this.filtersService.getCategoriesFilter(1, originalCategories, originalBrands));
		}
		
		if (originalBrands.length) {
			this._filters.push(this.filtersService.getBrandsFilter(2, originalBrands));
		}
		
		if (originalStatuses.length) {
			this._filters.push(this.filtersService.getPlanogramStatusesFilter(3, originalStatuses));
		}
		
		if (originalEquipmentTypes.length) {
			this._filters.push(this.filtersService.getEquipmentTypesFilter(4, originalEquipmentTypes, 'equipment_types'));
		}
		
		this._filters.push({
			id: FilterID.Radio,
			type: FilterType.Radio,
			param: 'has_products',
			label: 'PLANOGRAMS_LIST.HAS_PRODUCTS',
			displayOrder: 5,
			data: [
				{ id: -1, value: 'PLANOGRAMS_LIST.ALL_PLANOGRAMS' },
				{ id: 1, value: 'PLANOGRAMS_LIST.PLANOGRAMS_WITH_PRODUCTS' },
				{ id: 0, value: 'PLANOGRAMS_LIST.PLANOGRAMS_WITHOUT_PRODUCTS' }
			],
			selectingStrategy: new BoolSelectingStrategy()
		});
		
		setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
		this.filtersPanelService.setFilters(this._filters);

		this._subscriptions.push(
			this.filtersPanelService.getApplyPanel().subscribe((apply: boolean) => {
				this.filtersApplied = false;

				if (apply) {
					this._filters.forEach((filter: Filter) => {
						const updateParam: Object = {};
						if (
							filter.id === FilterID.ProductsHideOptions &&
							filter.selectingStrategy.getSelected().includes(-1)
						) {
							updateParam[filter.param] = filter.data[0].children.map((f: FilterItem) => f.id);
						} else {
							updateParam[filter.param] = filter.selectingStrategy.getSelected();
						}
						this._requestParams.updateParams(updateParam);
						if (filter.selectingStrategy.isSelected()) this.filtersApplied = true;
					});
				} else {
					this._filters.forEach((filter: Filter) => {
						const updateParam: Object = {};
						updateParam[filter.param] = null;
						this._requestParams.updateParams(updateParam);
					});
				}

				this._loadAddPlanograms$.next();
			}),

			this.filtersPanelService.getShowPanel().subscribe((show: boolean) => {
				if (!show) setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
			})
		);
	}

	public onScrollAdd(event: Event): void {
		const element = event.target as HTMLElement;
		if (
			element.scrollHeight - element.scrollTop - element.clientHeight < 400 &&
			this.addPlanogramsResponse()?.next &&
			!this.isAddLoading() &&
			!this.isAddLoadingNext()
		) {
			this.isAddLoadingNext.set(true);
			this.tableService.getNextTablePage('GET', this.addPlanogramsResponse().next).subscribe((response: ITableData<IPlanogramListItem>) => {
				this.addPlanogramsResponse.update((value) => {
					return {
						...value,
						...response,
						results: {
							...value.results,
							dataSource: [...value.results.dataSource, ...response.results.dataSource]
						}
					}
				});
				this.isAddLoadingNext.set(false);
			});
		}
	}

	public onClose(result: { changed: boolean; deleted: boolean, separateId?: number } = { changed: false, deleted: false }): void {
		this.data.onCloseResult?.(result);
		this.panelService.close();
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}